import { User, DatabaseUser, CreateUserRequest } from "../types";
import { supabase } from "./supabaseClient";
import { transformDatabaseUser } from "./dataTransformers";
import { emailTrackingService } from "./emailTrackingService";
import { handleError, withRetry, createAppError, ErrorCategory } from "./errorHandler";
import { logger } from "../lib/logger";
import { APP_CONSTANTS } from "../constants";
import {
    checkRateLimit,
    recordAttempt,
    clearRateLimit,
    type RateLimitStatus,
} from "./rateLimitService";

// ============================================
// CONSTANTS (Item 18 - authService optimization)
// ============================================

/** Session cache TTL - prevents unnecessary database queries */
const SESSION_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/** Minimum password length requirement */
const MIN_PASSWORD_LENGTH = 8;

/** Common passwords to reject */
const COMMON_PASSWORDS = ['password1', 'password123', '12345678', 'qwerty123', 'abc12345', 'letmein1', 'welcome1', 'admin123', 'monkey123', 'dragon12'];

// ============================================
// NOTE: Rate limiting is now handled by rateLimitService.ts
// which uses database-backed persistent storage.
// See: 20260125000100_database_rate_limiting.sql
// ============================================

// ============================================
// SESSION CACHE (Item 18 - authService optimization)
// ============================================

let cachedUser: User | null = null;
let cacheExpiry: number = 0;

// ============================================
// INTERFACES (Item 18 - authService optimization)
// ============================================

/** Session information for monitoring */
export interface SessionInfo {
    userId: string;
    email: string;
    expiresAt: Date | null;
    isExpired: boolean;
    refreshToken: boolean;
    provider: string;
}

/** Auth statistics for monitoring */
export interface AuthStatistics {
    sessionCacheHit: boolean;
    sessionCacheExpiry: Date | null;
    isAuthenticated: boolean;
    lastActivity: Date | null;
}

/** Validation result */
export interface ValidationResult {
    valid: boolean;
    errors: string[];
}

// ============================================
// FETCH TIMEOUT HELPER (Item 91 - Edge Function timeout handling)
// ============================================

/**
 * Fetch with AbortController timeout to prevent indefinite hangs
 * when Edge Functions become unresponsive.
 * @param url - The URL to fetch
 * @param options - Fetch options (method, headers, body)
 * @param timeoutMs - Timeout in milliseconds (defaults to APP_CONSTANTS.TIMEOUTS.FETCH_TIMEOUT)
 * @returns Response object
 * @throws Error with 'Request timeout' message if timeout exceeded
 */
export const fetchWithTimeout = async (
    url: string,
    options: RequestInit,
    timeoutMs: number = APP_CONSTANTS.TIMEOUTS.FETCH_TIMEOUT
): Promise<Response> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
        const response = await fetch(url, {
            ...options,
            signal: controller.signal,
        });
        return response;
    } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
            throw new Error(`Request timeout after ${timeoutMs}ms`);
        }
        throw error;
    } finally {
        clearTimeout(timeoutId);
    }
};

// ============================================
// VALIDATION HELPERS (Item 18 - authService optimization)
// ============================================

/**
 * RFC 5322 email validation regex
 * Validates most common email formats
 */
const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

/**
 * Validate email format
 */
export const validateEmail = (email: string): ValidationResult => {
    const errors: string[] = [];

    if (!email || typeof email !== 'string') {
        errors.push('Email is required');
    } else {
        const trimmed = email.trim().toLowerCase();
        if (trimmed.length === 0) {
            errors.push('Email cannot be empty');
        } else if (trimmed.length > 254) {
            errors.push('Email is too long (max 254 characters)');
        } else if (!EMAIL_REGEX.test(trimmed)) {
            errors.push('Invalid email format');
        }
    }

    return { valid: errors.length === 0, errors };
};

/**
 * Validate password strength
 */
export const validatePassword = (password: string): ValidationResult => {
    const errors: string[] = [];

    if (!password || typeof password !== 'string') {
        errors.push('Password is required');
    } else {
        if (password.length < MIN_PASSWORD_LENGTH) {
            errors.push(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
        }
        if (password.length > 128) {
            errors.push('Password is too long (max 128 characters)');
        }
        // Check for at least one number and one letter for basic strength
        if (!/[a-zA-Z]/.test(password)) {
            errors.push('Password must contain at least one letter');
        }
        if (!/[0-9]/.test(password)) {
            errors.push('Password must contain at least one number');
        }
        if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
            errors.push('Password must contain at least one special character');
        }
        if (COMMON_PASSWORDS.includes(password.toLowerCase())) {
            errors.push('This password is too common. Please choose a stronger password.');
        }
    }

    return { valid: errors.length === 0, errors };
};

// ============================================
// CACHE MANAGEMENT (Item 18 - authService optimization)
// ============================================

/**
 * Clear the session cache - called on logout and auth state changes
 */
export const clearSessionCache = (): void => {
    cachedUser = null;
    cacheExpiry = 0;
    logger.debug('Session cache cleared');
};

/**
 * Get current cache status for monitoring
 */
export const getSessionCacheStatus = (): { cached: boolean; expiresIn: number | null } => {
    const now = Date.now();
    if (cachedUser && now < cacheExpiry) {
        return { cached: true, expiresIn: cacheExpiry - now };
    }
    return { cached: false, expiresIn: null };
};

// NOTE: Auth state listeners should be managed in App.tsx with proper cleanup.
// The clearSessionCache() function is exported and can be called directly
// when auth state changes. See App.tsx useEffect for the managed listener.

export const authService = {
    /**
     * Sign in with magic link (passwordless OTP)
     * Sends a login link to the user's email - no password required
     */
    loginWithMagicLink: async (email: string): Promise<{ success: boolean; message: string }> => {
        // Input validation
        const emailValidation = validateEmail(email);
        if (!emailValidation.valid) {
            throw createAppError(
                new Error(emailValidation.errors.join(', ')),
                'authService.loginWithMagicLink'
            );
        }

        const normalizedEmail = email.trim().toLowerCase();

        // Check rate limiting
        const rateLimitStatus = await checkRateLimit(normalizedEmail);
        if (rateLimitStatus.isLocked) {
            logger.warn('Magic link BLOCKED due to rate limiting', {
                email: normalizedEmail,
                attemptCount: rateLimitStatus.attemptCount,
                remainingMinutes: rateLimitStatus.remainingMinutes,
            });

            throw createAppError(
                {
                    message: `Too many login attempts. Please try again in ${rateLimitStatus.remainingMinutes} minute${rateLimitStatus.remainingMinutes !== 1 ? 's' : ''}.`,
                    category: ErrorCategory.RATE_LIMIT,
                },
                'authService.loginWithMagicLink'
            );
        }

        // Record this attempt
        await recordAttempt(normalizedEmail);

        try {
            const { error } = await supabase.auth.signInWithOtp({
                email: normalizedEmail,
                options: {
                    // Redirect to the app after clicking the link
                    emailRedirectTo: `${window.location.origin}/auth/callback`,
                },
            });

            if (error) {
                if (error.message?.toLowerCase().includes('rate') || error.status === 429) {
                    throw createAppError(
                        { ...error, category: ErrorCategory.RATE_LIMIT },
                        'authService.loginWithMagicLink'
                    );
                }
                throw error;
            }

            logger.info('Magic link sent', { email: normalizedEmail });

            return {
                success: true,
                message: 'If an account exists with this email, you will receive a login link shortly.',
            };
        } catch (error) {
            handleError(error, "authService.loginWithMagicLink");
            throw error;
        }
    },

    /**
     * Sign out the current user
     * (Item 18 - authService optimization)
     */
    logout: async (): Promise<void> => {
        try {
            // Clear cache before signing out
            clearSessionCache();

            const { error } = await supabase.auth.signOut();
            if (error) {
                throw error;
            }

            logger.info('User logged out successfully');
        } catch (error) {
            handleError(error, "authService.logout");
            throw error;
        }
    },

    /**
     * Get the currently authenticated user
     * Uses in-memory cache to reduce database queries on repeated calls
     * (Item 18 - authService optimization)
     */
    getCurrentUser: async (): Promise<User | null> => {
        try {
            // Check cache first
            const now = Date.now();
            if (cachedUser && now < cacheExpiry) {
                logger.debug('Returning cached user', { userId: cachedUser.id });
                return cachedUser;
            }

            const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();

            if (authError || !authUser) {
                // Clear cache if no auth user
                clearSessionCache();
                return null;
            }

            // Fetch the user's profile from the database with retry
            const userProfile = await withRetry(
                async () => {
                    const { data: profile, error: profileError } = await supabase
                        .from("users")
                        .select("*")
                        .eq("id", authUser.id)
                        .single();

                    if (profileError) {
                        // If profile not found, don't retry - just return null
                        if (profileError.code === 'PGRST116') {
                            return null;
                        }
                        throw profileError;
                    }
                    return profile;
                },
                'authService.getCurrentUser.fetchProfile',
                { maxAttempts: 2 }
            );

            if (!userProfile) {
                // Clear cache if profile not found
                clearSessionCache();
                return null;
            }

            const user = transformDatabaseUser(userProfile as DatabaseUser);

            // Update cache
            cachedUser = user;
            cacheExpiry = now + SESSION_CACHE_TTL_MS;
            logger.debug('User cached', { userId: user.id, expiresIn: SESSION_CACHE_TTL_MS });

            return user;
        } catch (error) {
            handleError(error, "authService.getCurrentUser", { rethrow: false });
            return null;
        }
    },

    /**
     * Handle the auth callback after a magic link click.
     * Checks if a valid session was established from the URL token exchange.
     *
     * @returns Object with hasSession flag and optional error message
     */
    handleAuthCallback: async (): Promise<{ hasSession: boolean; userId?: string; error?: string }> => {
        try {
            const { data, error } = await supabase.auth.getSession();

            if (error) {
                return { hasSession: false, error: error.message };
            }

            if (data.session) {
                return { hasSession: true, userId: data.session.user.id };
            }

            return { hasSession: false, error: 'Login link expired or invalid. Please request a new one.' };
        } catch (_error) {
            return { hasSession: false, error: 'Something went wrong. Please try again.' };
        }
    },

    /**
     * Get current session information for monitoring
     * (Item 18 - authService optimization)
     */
    getSessionInfo: async (): Promise<SessionInfo | null> => {
        try {
            const { data: { session }, error } = await supabase.auth.getSession();

            if (error || !session) {
                return null;
            }

            const expiresAt = session.expires_at
                ? new Date(session.expires_at * 1000)
                : null;

            return {
                userId: session.user.id,
                email: session.user.email || '',
                expiresAt,
                isExpired: expiresAt ? expiresAt < new Date() : false,
                refreshToken: !!session.refresh_token,
                provider: session.user.app_metadata?.provider || 'email',
            };
        } catch (error) {
            handleError(error, "authService.getSessionInfo", { rethrow: false });
            return null;
        }
    },

    /**
     * Get authentication statistics for monitoring
     * (Item 18 - authService optimization)
     */
    getAuthStatistics: async (): Promise<AuthStatistics> => {
        const cacheStatus = getSessionCacheStatus();
        const sessionInfo = await authService.getSessionInfo();

        return {
            sessionCacheHit: cacheStatus.cached,
            sessionCacheExpiry: cacheStatus.expiresIn
                ? new Date(Date.now() + cacheStatus.expiresIn)
                : null,
            isAuthenticated: !!sessionInfo,
            lastActivity: null, // Could be tracked if needed
        };
    },

    /**
     * Refresh the current session proactively
     * (Item 18 - authService optimization)
     */
    refreshSession: async (): Promise<boolean> => {
        try {
            const { data: { session }, error } = await supabase.auth.refreshSession();

            if (error || !session) {
                logger.warn('Session refresh failed', { error: error?.message });
                clearSessionCache();
                return false;
            }

            // Clear cache to force re-fetch with new session
            clearSessionCache();
            logger.debug('Session refreshed successfully');
            return true;
        } catch (error) {
            handleError(error, "authService.refreshSession", { rethrow: false });
            return false;
        }
    },

    /**
     * Check if the current session is valid and not expired
     * (Item 18 - authService optimization)
     */
    isSessionValid: async (): Promise<boolean> => {
        try {
            const sessionInfo = await authService.getSessionInfo();
            return sessionInfo !== null && !sessionInfo.isExpired;
        } catch {
            return false;
        }
    },

    /**
     * Resend confirmation email using Supabase native resend
     * (Item 18 - authService optimization)
     */
    resendConfirmationEmail: async (email: string): Promise<void> => {
        const emailValidation = validateEmail(email);
        if (!emailValidation.valid) {
            throw createAppError(
                new Error(emailValidation.errors.join(', ')),
                'authService.resendConfirmationEmail'
            );
        }

        try {
            const { error } = await supabase.auth.resend({
                type: 'signup',
                email: email.trim().toLowerCase(),
            });

            if (error) {
                throw error;
            }

            logger.info('Confirmation email resent', { email });
        } catch (error) {
            handleError(error, "authService.resendConfirmationEmail");
            throw error;
        }
    },

    /**
     * Sign up a new user with magic link (passwordless OTP)
     * Creates the user if they don't exist and sends a login link.
     * Used by the SignUp form for self-service registration.
     *
     * @param email - User's email address
     * @param options - Metadata to attach to the new user (name, role, company_id)
     */
    signUpWithOtp: async (
        email: string,
        options: {
            name: string;
            role: string;
            company_id: string;
            emailRedirectTo?: string;
        }
    ): Promise<void> => {
        const emailValidation = validateEmail(email);
        if (!emailValidation.valid) {
            throw createAppError(
                new Error(emailValidation.errors.join(', ')),
                'authService.signUpWithOtp'
            );
        }

        const normalizedEmail = email.trim().toLowerCase();

        try {
            const { error } = await supabase.auth.signInWithOtp({
                email: normalizedEmail,
                options: {
                    shouldCreateUser: true,
                    emailRedirectTo: options.emailRedirectTo || `${window.location.origin}/auth/callback`,
                    data: {
                        name: options.name,
                        role: options.role,
                        company_id: options.company_id,
                    },
                },
            });

            if (error) throw error;

            logger.info('Magic link signup sent', { email: normalizedEmail });
        } catch (error) {
            handleError(error, 'authService.signUpWithOtp');
            throw error;
        }
    },

    /**
     * Resend magic link email for signup confirmation
     * Wraps supabase.auth.resend with type 'signup'
     *
     * @param email - The email to resend the magic link to
     */
    resendMagicLink: async (email: string): Promise<void> => {
        const emailValidation = validateEmail(email);
        if (!emailValidation.valid) {
            throw createAppError(
                new Error(emailValidation.errors.join(', ')),
                'authService.resendMagicLink'
            );
        }

        try {
            const { error } = await supabase.auth.resend({
                type: 'signup',
                email: email.trim().toLowerCase(),
            });

            if (error) throw error;

            logger.info('Magic link resent', { email });
        } catch (error) {
            handleError(error, 'authService.resendMagicLink');
            throw error;
        }
    },

    /**
     * Register a new user (typically called by admin invite)
     * Includes input validation and retry logic
     * (Item 18 - authService optimization)
     */
    registerUser: async (
        email: string,
        password: string,
        userData: CreateUserRequest
    ): Promise<User> => {
        // Input validation
        const emailValidation = validateEmail(email);
        if (!emailValidation.valid) {
            throw createAppError(
                new Error(emailValidation.errors.join(', ')),
                'authService.registerUser'
            );
        }

        const passwordValidation = validatePassword(password);
        if (!passwordValidation.valid) {
            throw createAppError(
                new Error(passwordValidation.errors.join(', ')),
                'authService.registerUser'
            );
        }

        // Validate user data
        if (!userData.name || userData.name.trim().length === 0) {
            throw createAppError(
                new Error('Name is required'),
                'authService.registerUser'
            );
        }

        if (!userData.role) {
            throw createAppError(
                new Error('Role is required'),
                'authService.registerUser'
            );
        }

        try {
            // Create auth user with metadata
            const { data: authData, error: authError } = await supabase.auth.signUp({
                email: email.trim().toLowerCase(),
                password,
                options: {
                    data: {
                        name: userData.name.trim(),
                        role: userData.role,
                        company_id: userData.company_id,
                        // assigned_complex_ids removed — use user_complex_assignments junction table
                    },
                },
            });

            if (authError || !authData.user) {
                throw authError || new Error("User registration failed");
            }

            // Profile is created automatically by database trigger on auth.users

            // ✅ EMAIL WORKFLOW (Updated Dec 2024):
            // 1. Supabase Auth automatically sends branded confirmation email
            //    - Configure template: Dashboard → Authentication → Email Templates → "Confirm signup"
            //    - Template file: supabase/templates/confirmation.html
            //    - Uses native {{ .ConfirmationURL }} variable
            //
            // 2. Admin notification sent AFTER email confirmation (not on signup)
            //    - Trigger: Database webhook on auth.users when email_confirmed_at changes
            //    - Setup: Dashboard → Database → Webhooks → notify-admin-new-signup
            //    - Only notifies admin about confirmed, real users (reduces spam)

            logger.info('User registered successfully', {
                userId: authData.user.id,
                email: email.trim().toLowerCase(),
                role: userData.role,
            });

            // If email confirmation is enabled, we won't have a session immediately
            if (!authData.session) {
                // Return a constructed user object since we can't fetch from DB (RLS)
                // Defensive initials generation with fallback for empty/whitespace names
                const initials = (userData.name?.trim() || 'U')
                    .split(' ')
                    .filter(part => part.length > 0)
                    .map(part => part[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2) || 'U';

                return {
                    id: authData.user.id,
                    name: userData.name.trim(),
                    email: email.trim().toLowerCase(),
                    role: userData.role,
                    initials: initials,
                    assignedComplexCount: 0, // Assignments managed via user_complex_assignments junction table
                    assignedComplexIds: [], // Populated via junction table after registration
                    companyId: userData.company_id,
                };
            }

            // If we have a session, fetch the created profile with retry
            const userId = authData.user.id;
            const userProfile = await withRetry(
                async () => {
                    const { data: profile, error: profileError } = await supabase
                        .from("users")
                        .select()
                        .eq("id", userId)
                        .single();

                    if (profileError || !profile) {
                        throw profileError || new Error("Profile not found");
                    }
                    return profile;
                },
                'authService.registerUser.fetchProfile',
                { maxAttempts: 3, baseDelayMs: 500 } // Give trigger time to run
            );

            return transformDatabaseUser(userProfile as DatabaseUser);
        } catch (error) {
            handleError(error, "authService.registerUser");
            throw error;
        }
    },

    /**
     * Send password reset email via Edge Function
     * Includes email validation and retry logic
     * (Item 18 - authService optimization)
     */
    resetPasswordForEmail: async (email: string): Promise<void> => {
        // Input validation
        const emailValidation = validateEmail(email);
        if (!emailValidation.valid) {
            throw createAppError(
                new Error(emailValidation.errors.join(', ')),
                'authService.resetPasswordForEmail'
            );
        }

        const normalizedEmail = email.trim().toLowerCase();

        try {
            await withRetry(
                async () => {
                    const response = await fetchWithTimeout(
                        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-reset-email`,
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                            },
                            body: JSON.stringify({ email: normalizedEmail }),
                        }
                    );

                    // Check response.ok BEFORE parsing JSON to avoid SyntaxError on HTML error pages
                    if (!response.ok) {
                        // Check for rate limiting
                        if (response.status === 429) {
                            throw createAppError(
                                { message: 'Too many requests', status: 429 },
                                'authService.resetPasswordForEmail'
                            );
                        }
                        const statusText = response.statusText || 'Unknown Error';
                        throw new Error(`HTTP ${response.status}: ${statusText}`);
                    }

                    const data = await response.json();

                    if (data.success) {
                        // Log email sent (don't await to not block)
                        emailTrackingService.logEmailSent({
                            emailId: data.emailId || `reset_${Date.now()}`,
                            userId: undefined,
                            recipient: normalizedEmail,
                            subject: 'Reset Your Password - AuthentipetsAPP',
                            fromAddress: 'AuthentipetsAPP <noreply@authentipets.com>',
                            emailType: 'reset',
                        }).catch(() => { /* ignore tracking errors */ });
                    }

                    return data;
                },
                'authService.resetPasswordForEmail',
                { maxAttempts: 2 }
            );

            logger.info('Password reset email sent', { email: normalizedEmail });
        } catch (error) {
            handleError(error, "authService.resetPasswordForEmail");
            throw error;
        }
    },

    /**
     * Update user password using reset token (used after clicking reset link)
     * Includes password validation
     * (Item 18 - authService optimization)
     */
    updateUserPassword: async (token: string, newPassword: string): Promise<void> => {
        // Validate token
        if (!token || token.length === 0) {
            throw createAppError(
                new Error('Reset token is required'),
                'authService.updateUserPassword'
            );
        }

        // Validate password
        const passwordValidation = validatePassword(newPassword);
        if (!passwordValidation.valid) {
            throw createAppError(
                new Error(passwordValidation.errors.join(', ')),
                'authService.updateUserPassword'
            );
        }

        try {
            await withRetry(
                async () => {
                    const response = await fetchWithTimeout(
                        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-reset-token`,
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                            },
                            body: JSON.stringify({ token, newPassword }),
                        }
                    );

                    // Check response.ok BEFORE parsing JSON to avoid SyntaxError on HTML error pages
                    if (!response.ok) {
                        const statusText = response.statusText || 'Unknown Error';
                        throw new Error(`HTTP ${response.status}: ${statusText}`);
                    }

                    const data = await response.json();
                    return data;
                },
                'authService.updateUserPassword',
                { maxAttempts: 2 }
            );

            logger.info('Password updated via reset token');
        } catch (error) {
            handleError(error, "authService.updateUserPassword");
            throw error;
        }
    },

    /**
     * Update user profile information (name, email)
     * Includes validation and proper cache invalidation
     * (Item 18 - authService optimization)
     */
    updateProfile: async (userId: string, updates: { name?: string; email?: string }): Promise<void> => {
        // Validate userId
        if (!userId || userId.length === 0) {
            throw createAppError(
                new Error('User ID is required'),
                'authService.updateProfile'
            );
        }

        // Validate email if provided
        if (updates.email) {
            const emailValidation = validateEmail(updates.email);
            if (!emailValidation.valid) {
                throw createAppError(
                    new Error(emailValidation.errors.join(', ')),
                    'authService.updateProfile'
                );
            }
        }

        // Validate name if provided
        if (updates.name !== undefined && updates.name.trim().length === 0) {
            throw createAppError(
                new Error('Name cannot be empty'),
                'authService.updateProfile'
            );
        }

        try {
            // Update auth email and metadata if changed
            const authUpdates: { email?: string; data?: { name: string } } = {};
            if (updates.email) authUpdates.email = updates.email.trim().toLowerCase();
            if (updates.name) authUpdates.data = { name: updates.name.trim() };

            if (Object.keys(authUpdates).length > 0) {
                const { error: authError } = await supabase.auth.updateUser(authUpdates);
                if (authError) throw authError;
            }

            // Update database profile with retry
            if (updates.name || updates.email) {
                await withRetry(
                    async () => {
                        const dbUpdates: { name?: string; email?: string } = {};
                        if (updates.name) dbUpdates.name = updates.name.trim();
                        if (updates.email) dbUpdates.email = updates.email.trim().toLowerCase();

                        const { error: dbError } = await supabase
                            .from('users')
                            .update(dbUpdates)
                            .eq('id', userId);

                        if (dbError) throw dbError;
                    },
                    'authService.updateProfile.database',
                    { maxAttempts: 2 }
                );
            }

            // Clear cache to force re-fetch with updated data
            clearSessionCache();

            logger.info('Profile updated', { userId, updates: Object.keys(updates) });
        } catch (error) {
            handleError(error, "authService.updateProfile");
            throw error;
        }
    },

    /**
     * Update password for authenticated user
     * Includes password validation
     * (Item 18 - authService optimization)
     */
    updatePassword: async (password: string): Promise<void> => {
        // Validate password
        const passwordValidation = validatePassword(password);
        if (!passwordValidation.valid) {
            throw createAppError(
                new Error(passwordValidation.errors.join(', ')),
                'authService.updatePassword'
            );
        }

        try {
            const { error } = await supabase.auth.updateUser({ password });
            if (error) throw error;

            logger.info('Password updated for authenticated user');
        } catch (error) {
            handleError(error, "authService.updatePassword");
            throw error;
        }
    },

    /**
     * Notify admin of new user signup
     * Calls the notify-admin-new-signup edge function directly
     * Includes retry logic for resilience
     * (Item 18 - authService optimization)
     */
    notifyAdminNewSignup: async (data: {
        userEmail: string;
        userName?: string;
        companyName?: string;
        userRole?: string;
        userId: string;
    }): Promise<void> => {
        try {
            await withRetry(
                async () => {
                    const response = await fetchWithTimeout(
                        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notify-admin-new-signup`,
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                            },
                            body: JSON.stringify({
                                userEmail: data.userEmail,
                                userName: data.userName || 'Not provided',
                                companyName: data.companyName || 'Not provided',
                                userRole: data.userRole || 'Not specified',
                                userId: data.userId,
                            }),
                        }
                    );

                    // Check response.ok BEFORE parsing JSON to avoid SyntaxError on HTML error pages
                    if (!response.ok) {
                        const statusText = response.statusText || 'Unknown Error';
                        throw new Error(`HTTP ${response.status}: ${statusText}`);
                    }

                    const result = await response.json();
                    return result;
                },
                'authService.notifyAdminNewSignup',
                { maxAttempts: 2, baseDelayMs: 500 }
            );

            logger.info('Admin notified of new signup', { email: data.userEmail });
        } catch (error) {
            // Don't throw - this shouldn't block user creation
            handleError(error, "authService.notifyAdminNewSignup", { rethrow: false });
        }
    },
};

// ============================================
// RATE LIMIT UTILITIES (Database-backed via rateLimitService)
// ============================================

/**
 * Rate limit status for UI display
 * Re-exported from rateLimitService for backwards compatibility
 */
export type { RateLimitStatus as LoginRateLimitStatus } from "./rateLimitService";

/**
 * Check if a user can attempt login (async, database-backed)
 * Useful for UI to show warnings or disable login button
 *
 * @param email - The email to check
 * @returns Promise<RateLimitStatus> with detailed info for UI
 */
export const getLoginRateLimitStatus = async (email: string): Promise<RateLimitStatus> => {
    return checkRateLimit(email);
};

/**
 * Rate limit configuration constants
 * Re-exported from rateLimitService for backwards compatibility
 */
export { RATE_LIMIT_CONFIG } from "./rateLimitService";

/**
 * Manually clear rate limit for a user (admin function)
 * Now uses database-backed persistent storage
 *
 * @param email - The email to clear rate limit for
 * @returns Promise<boolean> indicating if the clear was successful
 */
export const clearRateLimitForEmail = async (email: string): Promise<boolean> => {
    const result = await clearRateLimit(email);
    if (result) {
        logger.info('Rate limit cleared for email (admin action)', {
            email: email.toLowerCase(),
        });
    }
    return result;
};

/**
 * Get rate limit status for all rate limited emails (admin function)
 * Re-exported from rateLimitService
 */
export { getAllRateLimitedEmails } from "./rateLimitService";
